import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Minus, Plus, WifiOff, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { btnGold, errMsg, inp } from "@/components/attendance/ui";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export default function UsherCounter() {
  const { token } = useParams();
  const qKey = `hoh_att_queue_${token.slice(0, 12)}`;
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [zone, setZone] = useState(null);
  const [queue, setQueue] = useState(() => JSON.parse(localStorage.getItem(qKey) || "[]"));
  const [offline, setOffline] = useState(false);
  const queueRef = useRef(queue);
  const flushing = useRef(false);

  const saveQueue = (q) => { queueRef.current = q; setQueue(q); localStorage.setItem(qKey, JSON.stringify(q)); };
  const apply = (data) => { setS(data); setZone((z) => z || data.zones[0]?.id); setOffline(false); };

  const flush = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    const batch = queueRef.current.slice(0, 200);
    try {
      const r = batch.length ? await api.post(`/attendance/usher/${token}/ops`, { ops: batch }) : await api.get(`/attendance/usher/${token}`);
      const done = new Set(batch.map((o) => o.op_id));
      saveQueue(queueRef.current.filter((o) => !done.has(o.op_id)));
      apply(r.data);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403 || st === 404) setError(errMsg(e, "This link is no longer valid."));
      else if (st === 409 || st === 402) {
        if (batch.length) toast.warning(`${batch.length} tap${batch.length === 1 ? " was" : "s were"} not saved — counting is closed or paused.`);
        saveQueue([]); setError(null); const r = await api.get(`/attendance/usher/${token}`).catch(() => null); if (r) apply(r.data);
      }
      else setOffline(true);
    } finally { flushing.current = false; }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { flush(); const t = setInterval(() => { if (!document.hidden) flush(); }, 3000); return () => clearInterval(t); }, [flush]);
  useEffect(() => { const on = () => flush(); window.addEventListener("online", on); return () => window.removeEventListener("online", on); }, [flush]);

  if (error) return <Shell><p className="text-center text-neutral-300 mt-24 px-6" data-testid="usher-error">{error}</p></Shell>;
  if (!s) return <Shell><div className="flex justify-center mt-32"><Loader2 className="animate-spin text-[#D4AF37]" /></div></Shell>;
  if (!s.usher.name) return <NamePrompt token={token} onDone={apply} />;

  const pending = (z, c) => queue.filter((o) => o.zone_id === z && o.category === c).reduce((n, o) => n + o.delta, 0);
  const count = (z, c) => Math.max(0, (s.zone_counts[z]?.[c] || 0) + pending(z, c));
  const tap = (c, delta) => {
    if (!s.can_count || (delta < 0 && count(zone, c) <= 0)) return;
    if (navigator.vibrate) navigator.vibrate(12);
    saveQueue([...queueRef.current, { op_id: uid(), zone_id: zone, category: c, delta }]);
    setTimeout(flush, 150);
  };
  const zoneTotal = s.event.categories.reduce((n, c) => n + count(zone, c.key), 0);
  const eventTotal = s.total + queue.reduce((n, o) => n + o.delta, 0);

  return (
    <Shell>
      <header className="px-5 pt-6 pb-4 border-b border-white/10">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">{s.org.name}</div>
        <h1 className="font-serif-display text-2xl text-white mt-1 leading-tight" data-testid="usher-event-name">{s.event.name}</h1>
        <div className="text-xs text-neutral-400 mt-1">{s.event.date} {s.event.start_time} · Usher: {s.usher.name}</div>
        <Status s={s} offline={offline} pending={queue.length} />
      </header>
      {s.zones.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-5 py-3 border-b border-white/5">
          {s.zones.map((z) => (
            <button key={z.id} onClick={() => setZone(z.id)} data-testid={`usher-zone-${z.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm border ${zone === z.id ? "border-[#D4AF37] bg-[#D4AF37] text-black font-semibold" : "border-white/15 text-neutral-300"}`}>{z.name}</button>
          ))}
        </div>
      )}
      <main className="px-5 py-5 space-y-3 flex-1">
        {s.event.categories.map((c) => (
          <div key={c.key} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121212] p-3" data-testid={`usher-row-${c.key}`}>
            <button aria-label={`Remove ${c.label}`} onClick={() => tap(c.key, -1)} disabled={!s.can_count || count(zone, c.key) <= 0} data-testid={`usher-minus-${c.key}`}
              className="w-16 h-16 rounded-xl border border-white/15 flex items-center justify-center text-white active:scale-95 transition-transform disabled:opacity-25"><Minus size={26} /></button>
            <div className="flex-1 text-center">
              <div className="text-[11px] tracking-widest uppercase text-neutral-400">{c.label}</div>
              <div className="font-serif-display text-5xl text-white tabular-nums leading-none mt-1" data-testid={`usher-count-${c.key}`}>{count(zone, c.key)}</div>
            </div>
            <button aria-label={`Add ${c.label}`} onClick={() => tap(c.key, 1)} disabled={!s.can_count} data-testid={`usher-plus-${c.key}`}
              className="w-16 h-16 rounded-xl bg-[#D4AF37] text-black flex items-center justify-center active:scale-95 transition-transform disabled:opacity-25"><Plus size={28} /></button>
          </div>
        ))}
      </main>
      <footer className="sticky bottom-0 grid grid-cols-2 border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur">
        <div className="p-4 text-center border-r border-white/10"><div className="text-[10px] tracking-widest uppercase text-neutral-500">My zone</div><div className="font-serif-display text-3xl text-white" data-testid="usher-zone-total">{zoneTotal}</div></div>
        <div className="p-4 text-center"><div className="text-[10px] tracking-widest uppercase text-neutral-500">Whole event · live</div><div className="font-serif-display text-3xl text-[#D4AF37]" data-testid="usher-event-total">{eventTotal}</div></div>
      </footer>
    </Shell>
  );
}

function Status({ s, offline, pending }) {
  if (s.event.status !== "open") return <div className="mt-3 text-xs text-amber-300" data-testid="usher-status">{s.event.status === "closed" ? "Counting has closed for this event. Thank you!" : "Counting hasn't opened yet — please wait for the organiser."}</div>;
  if (!s.can_count) return <div className="mt-3 text-xs text-amber-300" data-testid="usher-status">Counting is paused by the organisation.</div>;
  if (offline) return <div className="mt-3 text-xs text-amber-300 inline-flex items-center gap-1" data-testid="usher-status"><WifiOff size={12} /> Offline — {pending} tap{pending === 1 ? "" : "s"} saved, will sync automatically</div>;
  return <div className="mt-3 text-xs text-emerald-300 inline-flex items-center gap-1" data-testid="usher-status"><CheckCircle2 size={12} /> Live {pending ? `· syncing ${pending}` : "· synced"}</div>;
}

function NamePrompt({ token, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post(`/attendance/usher/${token}/name`, { name }); onDone(r.data); }
    catch (err) { toast.error(errMsg(err, "Couldn't save your name — check your connection and try again")); setBusy(false); }
  };
  return (
    <Shell>
      <form onSubmit={submit} className="px-6 mt-24 space-y-4" data-testid="usher-name-form">
        <h1 className="font-serif-display text-3xl text-white">Welcome, usher</h1>
        <p className="text-sm text-neutral-400">Enter your name so the organiser knows who counted.</p>
        <input required autoFocus className={inp} data-testid="usher-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
        <button disabled={busy} className={`${btnGold} w-full py-3.5`} data-testid="usher-name-submit">Start counting</button>
      </form>
    </Shell>
  );
}

const Shell = ({ children }) => <div className="min-h-[100svh] bg-[#0A0A0A] text-white flex flex-col max-w-lg mx-auto select-none" data-testid="usher-counter">{children}</div>;
